import { Directive, ElementRef, Input, OnChanges, OnDestroy, AfterViewInit, SimpleChanges, HostListener, forwardRef, Injector } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs/Subscription';

// imask v6 ships TypeScript 2.8+ conditional types in its index.d.ts which TS 2.6 can't parse.
// Use require() to bypass TS's type loading entirely; treat IMask as `any`.
declare const require: any;
// tslint:disable-next-line:no-var-requires
const _imaskModule: any = require('imask');
// imask is an ES module with default export; require() returns { default: IMask, ... } in webpack.
const IMask: any = _imaskModule && _imaskModule.default ? _imaskModule.default : _imaskModule;

/**
 * Visual-only input mask backed by imask.
 *
 * - The user sees the formatted value in the DOM ('0412 5515168').
 * - The form control receives the *unmasked* value ('04125515168').
 * - Pasted text is normalized via imask before the form control sees it.
 *
 * Defensive design: registers as NG_VALUE_ACCESSOR AND injects NgControl so that even if Angular
 * picks the DefaultValueAccessor over us (or some other code writes the formatted value to the
 * form control), we re-correct via a valueChanges subscription.
 *
 * Usage: <input [formControl]="ctrl" [rmtVisualMask]="'0000 0000000'">
 */
@Directive({
  selector: '[rmtVisualMask]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VisualMaskDirective),
      multi: true,
    },
  ],
})
export class VisualMaskDirective implements ControlValueAccessor, OnChanges, OnDestroy, AfterViewInit {
  @Input('rmtVisualMask') maskPattern: string = '';
  /** Optional preprocessor for pasted raw text. Lets callers strip country codes or separators that would otherwise overflow the mask. */
  @Input('rmtVisualMaskPrepare') prePaste: ((raw: string) => string) | undefined;
  private mask: any = null;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private pendingValue: string | null = null;
  private valueChangesSub: Subscription | null = null;
  private correcting = false;
  private ngControl: NgControl | null = null;

  constructor(
    private el: ElementRef,
    private injector: Injector,
  ) {}

  ngAfterViewInit() {
    // Defer NgControl resolution to avoid the cyclic dependency: this directive provides
    // NG_VALUE_ACCESSOR (which NgControl looks up), so it can't inject NgControl directly in the constructor.
    try {
      this.ngControl = this.injector.get(NgControl, null as any);
    } catch (_) {
      this.ngControl = null;
    }
    this.applyMask();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['maskPattern'] && this.el.nativeElement) {
      this.applyMask();
    }
  }

  ngOnDestroy() {
    this.destroyMask();
    if (this.valueChangesSub) {
      this.valueChangesSub.unsubscribe();
      this.valueChangesSub = null;
    }
  }

  // ControlValueAccessor

  writeValue(value: any): void {
    const incoming = value == null ? '' : String(value);
    if (this.mask) {
      if (this.mask.unmaskedValue !== incoming) {
        this.mask.unmaskedValue = incoming;
      }
    } else {
      this.pendingValue = incoming;
      const input = this.el.nativeElement as HTMLInputElement;
      if (input && input.tagName === 'INPUT') input.value = incoming;
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const input = this.el.nativeElement as HTMLInputElement;
    if (input && input.tagName === 'INPUT') input.disabled = isDisabled;
  }

  @HostListener('input', ['$event.target.value'])
  handleInput(value: any) {
    if (this.mask) return; // imask `accept` will fire and call onChange with unmasked value
    this.onChange(value);
  }

  @HostListener('blur')
  handleBlur() {
    this.onTouched();
  }

  @HostListener('paste', ['$event'])
  handlePaste(e: ClipboardEvent) {
    if (!this.mask || !this.prePaste) return;
    e.preventDefault();
    const cd: any = (e as any).clipboardData || (window as any).clipboardData;
    if (!cd) return;
    const raw = cd.getData('text') as string;
    const normalized = this.prePaste(raw);
    // Feed normalized digits into imask; this triggers `accept` which propagates onChange(unmasked) to the form control.
    this.mask.unmaskedValue = normalized || '';
  }

  // imask lifecycle

  private applyMask() {
    this.destroyMask();
    if (!this.maskPattern) return;
    const input = this.el.nativeElement as HTMLInputElement;
    if (!input || input.tagName !== 'INPUT') return;

    this.mask = IMask(input, { mask: this.maskPattern });

    this.mask.on('accept', () => {
      const unmasked = this.mask.unmaskedValue;
      this.onChange(unmasked);
      // Defensive: if the form control's value diverges (e.g. DefaultValueAccessor wrote the formatted value),
      // re-correct without re-emitting (so writeValue doesn't loop, and valueChanges doesn't re-fire).
      if (this.ngControl && this.ngControl.control && this.ngControl.control.value !== unmasked) {
        this.correcting = true;
        this.ngControl.control.setValue(unmasked, { emitEvent: false });
        this.correcting = false;
      }
    });

    // Initial sync
    if (this.pendingValue !== null) {
      this.mask.unmaskedValue = this.pendingValue;
      this.pendingValue = null;
    } else if (input.value) {
      this.mask.unmaskedValue = input.value;
    }

    // Watch the form control for any external writes that don't match the mask, and correct them.
    if (this.ngControl && this.ngControl.control && this.ngControl.valueChanges) {
      this.valueChangesSub = this.ngControl.valueChanges.subscribe(value => {
        if (this.correcting || !this.mask) return;
        const ctrl = this.ngControl && this.ngControl.control;
        if (!ctrl) return;
        const expected = this.mask.unmaskedValue;
        if (value !== expected) {
          this.correcting = true;
          ctrl.setValue(expected, { emitEvent: false });
          this.correcting = false;
        }
      });
    }
  }

  private destroyMask() {
    if (this.mask) {
      this.mask.destroy();
      this.mask = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/directives/visual-mask/visual-mask.directive.ts